import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';
import {
  formatTimeString,
  generateTimeOptions,
  parseTimeInput,
  timeStringToMinutes,
} from '../../utils/timeUtils';

interface TimeOfDayPickerProps {
  /** Current value in 24-hour "HH:MM" format. */
  value: string;
  /** Fired with the next 24-hour "HH:MM" value. */
  onChange: (value: string) => void;
  /** Optional className for the trigger button. */
  className?: string;
  /** Optional accessible label (also used as placeholder when value is empty). */
  ariaLabel?: string;
  /** Disable the trigger entirely (e.g. while booking is submitting). */
  disabled?: boolean;
  /** Earliest selectable time in 24-hour "HH:MM" format (inclusive). */
  minTime?: string;
  /** Latest selectable time in 24-hour "HH:MM" format (inclusive). */
  maxTime?: string;
}

/** Spacing between the quick-pick options in the scroll list. */
const OPTION_STEP_MINUTES = 15;

/** Popover width / approximate height — used for viewport-edge clamping. */
const POPOVER_WIDTH = 224;
const POPOVER_HEIGHT_ESTIMATE = 320;
const POPOVER_GAP = 8;
const VIEWPORT_PADDING = 8;

/** Clamp a "HH:MM" value into the [minTime, maxTime] window when bounds exist. */
const clampToBounds = (timeStr: string, minTime?: string, maxTime?: string): string => {
  const mins = timeStringToMinutes(timeStr);
  if (mins === null) return timeStr;
  const minM = timeStringToMinutes(minTime ?? null);
  const maxM = timeStringToMinutes(maxTime ?? null);
  if (minM !== null && mins < minM) return minTime!.slice(0, 5);
  if (maxM !== null && mins > maxM) return maxTime!.slice(0, 5);
  return timeStr;
};

/**
 * Google-Calendar-style time-of-day picker: a text field the admin can type
 * any time into ("9:30a", "12:35 pm", "1435"), backed by a scrollable list of
 * quick-pick times. Renders the popover into a `document.body` portal with
 * fixed positioning so it floats over scrollable modal bodies without being
 * clipped by `overflow: auto` ancestors — which is exactly what happens
 * inside CreateBookingModal's slot screen.
 */
export const TimeOfDayPicker: React.FC<TimeOfDayPickerProps> = ({
  value,
  onChange,
  className,
  ariaLabel,
  disabled,
  minTime,
  maxTime,
}) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);

  const options = useMemo(() => {
    const minM = timeStringToMinutes(minTime ?? null);
    const maxM = timeStringToMinutes(maxTime ?? null);
    const list = generateTimeOptions(OPTION_STEP_MINUTES).filter(({ value: v }) => {
      const m = timeStringToMinutes(v);
      if (m === null) return false;
      if (minM !== null && m < minM) return false;
      if (maxM !== null && m > maxM) return false;
      return true;
    });
    // Off-step current values (e.g. 12:35) still deserve a highlighted row,
    // but never re-insert a time that sits outside work-hour bounds.
    const current = value?.slice(0, 5);
    const currentM = timeStringToMinutes(current ?? null);
    const currentInBounds =
      currentM !== null &&
      (minM === null || currentM >= minM) &&
      (maxM === null || currentM <= maxM);
    if (current && currentInBounds && !list.some((o) => o.value === current)) {
      list.push({ value: current, label: formatTimeString(current) });
      list.sort((a, b) => (timeStringToMinutes(a.value) ?? 0) - (timeStringToMinutes(b.value) ?? 0));
    }
    return list;
  }, [minTime, maxTime, value]);

  const selectedValue = value?.slice(0, 5);

  /** Parse the typed text; commit when it's a valid time (clamped into bounds). */
  const commitText = useCallback(
    (raw: string): boolean => {
      const parsed = parseTimeInput(raw);
      if (!parsed) return false;
      const next = clampToBounds(parsed, minTime, maxTime);
      if (next !== value) onChange(next);
      return true;
    },
    [minTime, maxTime, value, onChange],
  );

  const typedPreview = useMemo(() => {
    const parsed = parseTimeInput(text);
    if (!parsed) return null;
    return clampToBounds(parsed, minTime, maxTime);
  }, [text, minTime, maxTime]);

  // Recalculate position on open, scroll, and resize so the popover stays
  // anchored to the trigger even as the booking modal's body scrolls.
  const recomputePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < POPOVER_HEIGHT_ESTIMATE && spaceAbove > spaceBelow;

    let top = openUpward
      ? rect.top - POPOVER_HEIGHT_ESTIMATE - POPOVER_GAP
      : rect.bottom + POPOVER_GAP;

    // Clamp vertically so we never spill off-screen on either edge —
    // matters when the trigger is near the very top or bottom of the
    // viewport and neither side has full height available.
    top = Math.max(VIEWPORT_PADDING, Math.min(top, vh - POPOVER_HEIGHT_ESTIMATE - VIEWPORT_PADDING));

    const width = Math.max(POPOVER_WIDTH, rect.width);
    let left = rect.left;
    if (left + width + VIEWPORT_PADDING > vw) {
      left = vw - width - VIEWPORT_PADDING;
    }
    if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;

    setPopoverPos({ top, left });
  }, []);

  useEffect(() => {
    if (!open) return;
    recomputePosition();
    const onScroll = () => recomputePosition();
    const onResize = () => recomputePosition();
    // Capture-phase scroll so we catch all scrolling ancestors, not just window.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, recomputePosition]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!(e.target instanceof Node)) return;
      if (triggerRef.current?.contains(e.target)) return;
      if (popoverRef.current?.contains(e.target)) return;
      commitText(inputRef.current?.value ?? '');
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, commitText]);

  // Seed the input and scroll the current time into view every open.
  useEffect(() => {
    if (!open) return;
    setText(value ? formatTimeString(value.slice(0, 5)) : '');
    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
      listRef.current
        ?.querySelector('[data-selected="true"]')
        ?.scrollIntoView({ block: 'center' });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, value]);

  const pick = (next: string) => {
    if (next !== value) onChange(next);
    setOpen(false);
  };

  const displayLabel = value ? formatTimeString(value) : ariaLabel || 'Pick a time';

  const popover = open && popoverPos && (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Pick a time"
      style={{
        position: 'fixed',
        top: popoverPos.top,
        left: popoverPos.left,
        width: Math.max(POPOVER_WIDTH, triggerRef.current?.getBoundingClientRect().width ?? 0),
        zIndex: 10000,
      }}
      className="rounded-xl border border-ink-200 bg-surface shadow-xl overflow-hidden flex flex-col"
    >
      <div className="p-2 border-b border-ink-100">
        {/* eslint-disable-next-line no-restricted-syntax -- compact popover input; the Input primitive's label/size chrome doesn't fit here */}
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (commitText(text)) setOpen(false);
            }
          }}
          placeholder="e.g. 9:30 AM"
          aria-label="Type a time"
          className="w-full px-2.5 py-1.5 text-sm font-medium tabular-nums text-ink-900 placeholder:text-ink-300 bg-ink-50 border border-transparent rounded-lg focus:outline-none focus:bg-surface focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-colors"
        />
        {typedPreview && typedPreview !== selectedValue && (
          <p className="px-1 pt-1 text-2xs text-ink-400">
            Press Enter for {formatTimeString(typedPreview)}
          </p>
        )}
      </div>

      <div ref={listRef} className="max-h-56 overflow-y-auto py-1" role="listbox">
        {options.map(({ value: v, label }) => {
          const isSelected = v === selectedValue;
          return (
            // eslint-disable-next-line no-restricted-syntax -- listbox option rows; Button/MenuItem primitives don't cover selected listbox styling
            <button
              key={v}
              type="button"
              role="option"
              aria-selected={isSelected}
              data-selected={isSelected || undefined}
              onClick={() => pick(v)}
              className={`w-full text-left px-3.5 py-1.5 text-sm tabular-nums transition-colors ${
                isSelected
                  ? 'bg-accent-600 text-white font-semibold'
                  : 'text-ink-700 font-medium hover:bg-ink-50'
              }`}
            >
              {label}
            </button>
          );
        })}
        {options.length === 0 && (
          <p className="px-3.5 py-2 text-xs text-ink-400">No times available</p>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* eslint-disable-next-line no-restricted-syntax -- combobox-style trigger with custom popover anchoring; ui primitives don't cover this */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel || 'Pick a time'}
        className={
          className ||
          `w-full flex items-center justify-between gap-2 px-3 py-2 text-sm font-medium border rounded-lg bg-surface transition-colors ${
            open ? 'border-accent-400 ring-2 ring-accent-200' : 'border-ink-200 hover:border-ink-300'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
        }
      >
        <span className="flex items-center gap-2 min-w-0">
          <Clock className="h-4 w-4 text-ink-400 flex-shrink-0" />
          <span className="tabular-nums text-ink-900 truncate">{displayLabel}</span>
        </span>
        <svg
          className={`h-3.5 w-3.5 text-ink-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.24 4.38a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {popover && createPortal(popover, document.body)}
    </>
  );
};

export default TimeOfDayPicker;
